import React, { useEffect, createRef } from 'react'
import { useDispatch } from 'react-redux';
import LaoImportKpiWrapper from 'components/LAO/LaoImportKpiWrapper'
import LaoExportKpiWrapper from 'components/LAO/LaoExportKpiWrapper'
import LaoTimeLineKpiWrapper from 'components/LAO/LaoTimelineKpiWrapper';
import LaoLeadTimeKpiWrapper from 'components/LAO/LaoLeadTimeKpiWrapper';
import LaoFilterHeader from 'components/LAO/LaoFilterHeader'
import useSetQuery, { useMultiSetQuery } from 'customHooks/useQuery';
import Typography from '@material-ui/core/Typography';
import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { useTranslation } from "react-i18next";
import { laoExportFilters, laoImportFilters, laoTimelineFilters, laoLeadTimeFilters } from 'reduxLib/constdata/filters';
import { resetFilters, saveFilters } from 'reduxLib/services';
import LaoTable from 'components/LAO/LaoTable';
import { endpoints } from 'helpers';
import { laoSearchType } from 'configs/appConstants';
import { laoImportOrderDetailsColumns } from 'reduxLib/constdata/LAO/laoImportOrderDetailsColumns';
import { laoOrderDetailsColumns } from 'reduxLib/constdata/LAO/laoOrderDetailsColumns';

const useStyles = makeStyles(theme => ({
  volume: {
    display: 'inline-block',
    whiteSpace: 'nowrap',
    marginRight: 16,
  },
  active: {
    opacity: "100%",
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'center'
    }
  },
  faded: {
    opacity: "30%",
    cursor: 'pointer',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'center'
    }
  }
}));


const LaoKPI = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const tableRef = createRef();
  const setMultiQuery = useMultiSetQuery();
  const dispatch = useDispatch();
  const [params, setParamsLao] = useSetQuery();
  const [activeTab, setActiveTab] = useState(params?.kpiType || "import");

  const tabDetails = {
    import: {
      subtype: 'importFilters',
      filterType: 'laoImportFilters',
      filter: laoImportFilters,
      tableEndpoint: endpoints.lao.laoTableImport,
      tableType: 'importKPIDetails',
      columns: laoImportOrderDetailsColumns,
      tableSubType: laoSearchType.importOrderDetails,
      searchText: t('lao_order_details_search_helper_text_import'),
      menuItem: 'orders' 
    },
    export: {
      subtype: 'exportFilters',
      filterType: 'laoExportFilters',
      filter: laoExportFilters,
      tableEndpoint: endpoints.lao.laoTableExport,
      tableType: 'exportKPIDetails',
      columns: laoOrderDetailsColumns,
      tableSubType: laoSearchType.exportOrderDetails,
      searchText: t('lao_order_details_search_helper_text'),
      menuItem: 'orders'
    },
    timeline: {
      subtype: 'timelineFilters',
      filterType: 'laoTimelineFilters',
      filter: laoTimelineFilters,
    },
    leadtime: {
      subtype: 'leadtimeFilters',
      filterType: 'laoLeadTimeFilters',
      filter: laoLeadTimeFilters
    }
  }

  const clearFiltersFromStore = () => {
    dispatch(resetFilters({ type: 'lao', subtype: 'importFilters' }));
    dispatch(resetFilters({ type: 'lao', subtype: 'exportFilters' }));
    dispatch(resetFilters({ type: 'lao', subtype: 'timelineFilters' }));
    dispatch(resetFilters({ type: 'lao', subtype: 'leadtimeFilters' }));
  }

  // Helper to clear laoFilters from URL and store
  const clearLaoFiltersFromUrlAndStore = () => {
    setMultiQuery({
      loadView: undefined,
      laoFilters: "{}",
      kpiType: activeTab,
      timelineType: "posoItems"
    });
    // Clear laoFilters from Redux store
    dispatch(saveFilters({
      filter: {
        ...tabDetails[activeTab]?.filter
      },
      type: 'lao',
      subtype: tabDetails[activeTab]?.subtype
    }));
  };

  // Resets filters on componnet unmount
  useEffect(() => clearFiltersFromStore, []);

  useEffect(() => {
    if (params?.loadView) {
      clearLaoFiltersFromUrlAndStore();
    }
  }, [params?.loadView]);

  const clearAllFiltersLao = () => {
    setParamsLao({ key: "laoFilters", value: {} })
  };

  const headerProps = {
    filterType: tabDetails[activeTab]?.filterType,
    type: 'lao',
    clearAllFilters: clearAllFiltersLao,
    filterExclusionArray: ['searchStringList'],
    searchText: '',
    subtype: tabDetails[activeTab]?.subtype,
    defaultFilters: tabDetails[activeTab]?.filter
  }

  const kpiTabComponent = {
    import: <LaoImportKpiWrapper defaultFilters={laoImportFilters} />,
    export: <LaoExportKpiWrapper defaultFilters={laoExportFilters} />,
    timeline: <LaoTimeLineKpiWrapper defaultFilters={laoTimelineFilters} />,
    leadtime: <LaoLeadTimeKpiWrapper defaultFilters={laoLeadTimeFilters} />
  }

  const handleTabChange = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      // clearLaoFiltersFromUrlAndStore() and set default filters for lead time tab
      tab === 'leadtime'
        ? setMultiQuery({
          loadView: undefined,
          kpiType: tab,
          laoFilters: JSON.stringify({
            importOrderOriginCountryList: ['PE'],
            importOrderDestinationCountryList: ['CL'],
            importShipmentModeList: ['M'],
            processTypeList: ['IC']
          }),
        })
        : setMultiQuery({ loadView: true });
    }
  }

  return (
    <div>
      <Grid item sm={12} >
        <Grid container item spacing={2}>
          <Grid item xs={12} sm={2} className={classes.volume}>
            <Typography
              data-testid="importKPI"
              variant="h3"
              className={activeTab === "import" ? classes.active : classes.faded}
              onClick={() => handleTabChange("import")}
            >
              {t("import_KPI")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={2} className={classes.volume}>
            <Typography
              data-testid="exportKPI"
              variant="h3"
              className={activeTab === "export" ? classes.active : classes.faded}
              onClick={() => handleTabChange("export")}
            >
              {t("export_KPI")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={2} className={classes.volume}>
            <Typography
              data-testid="timelineKPI"
              variant="h3"
              className={activeTab === "timeline" ? classes.active : classes.faded}
              onClick={() => handleTabChange("timeline")}
            >
              {t("timeline_KPI")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={2} className={classes.volume}>
            <Typography
              data-testid="leadtimeKPI"
              variant="h3"
              className={activeTab === "leadtime" ? classes.active : classes.faded}
              onClick={() => handleTabChange("leadtime")}
            >
              {t("leadtime_KPI")}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <LaoFilterHeader {...headerProps} />
      {kpiTabComponent[activeTab]}
      {["import", "export"].includes(activeTab) && <LaoTable
        fetchEndPoint={tabDetails[activeTab]?.tableEndpoint}
        columns={tabDetails[activeTab]?.columns}
        tableName={tabDetails[activeTab]?.tableType}
        subType={tabDetails[activeTab]?.tableSubType}
        filterType={tabDetails[activeTab]?.filterType}
        tableRef={tableRef}
        filterExclusionArray={['searchStringList']}
        searchText={tabDetails[activeTab]?.searchText}
        menuItem={tabDetails[activeTab]?.menuItem}
        showFilter={false}
        downloadAll={true}
        translationFields={['searchSubType']}
      />}
    </div>
  );
}

export default LaoKPI